/* eslint-disable */
import {
  Card,
  Spin, Typography
} from 'antd';
import { AxiosError } from 'axios';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import ChartCustom from 'common/components/Chart';
import { getKioskCumulativeByMonthV3Service } from 'common/services/dashboard';
import { formatNumberWithSeparators } from 'common/utils/functions';
import moment from 'moment';

interface NationwideByAccumProps {
  dateRange: string[];
  typeParam: number | undefined;
  kiosGroupId: number[];
  setRefetchFunc: (fn: () => void) => void
}

const NationwideByAccum: React.FC<NationwideByAccumProps> = ({
  dateRange,
  typeParam,
  kiosGroupId,
  setRefetchFunc
}) => {
  const { t } = useTranslation();
  const [isErrorTimeout, setIsErrorTimeout] = React.useState<boolean>(false);

  const {
    data: cumulativeByMonthData, isFetching, refetch
  } = useQuery(
    ['getKioskCumulativeByMonthV3Service'],
    () => getKioskCumulativeByMonthV3Service({
      from: dateRange[0],
      to: dateRange[1],
      ...(typeParam && { type: typeParam }),
      ...(kiosGroupId.join(',') && { kioskGroupIds: kiosGroupId.join(',') }),
    }),
    {
      onError: (err: AxiosError) => {
        if (err.response?.status === 408 || err.response?.status === 504) {
          setIsErrorTimeout(true);
        }
      },
      onSuccess: () => {
        setIsErrorTimeout(false);
      }
    }
  );

  const data = useMemo(() => ({
    labels: cumulativeByMonthData?.map(
      // (item) => `T${item.yearMonth.split('-')[1].replace(/^0/, '')}`
      (item) => `T${moment(item.yearMonth, 'YYYY-MM').format('M/YY')}`
    ) || [],
    datasets: [
      {
        data: cumulativeByMonthData?.map((item) => item.total) || [],
        borderColor: '#6A02D5',
        fill: true,
        tension: 0.3,
        backgroundColor: (context: any) => {
          const { chart } = context;
          const { ctx, chartArea } = chart;

          if (!chartArea) {
            return null;
          }

          const gradient = ctx.createLinearGradient(
            0,
            chartArea.top,
            0,
            chartArea.bottom
          );
          gradient.addColorStop(0, 'rgba(106, 2, 213, 0.4)'); // top (near line)
          gradient.addColorStop(1, 'rgba(106, 2, 213, 0)'); // bottom (transparent)
          return gradient;
        },
      },
    ],
  }), [cumulativeByMonthData]);

  const options = useMemo(() => {
    return {
      layout: {
        padding: {
          top: 25
        }
      },
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          display: (context: any) => Number(cumulativeByMonthData?.length) > 12 ? (context.dataIndex % 2 === 1) : undefined,
          color: '#333',
          anchor: 'end',
          align: 'top',
          formatter: (value: any) => formatNumberWithSeparators(value),
        },
        tooltip: {
          enabled: true, // still show value on hover
        },
      },
      responsive: true,
      scales: {
        y: {
          title: {
            display: true,
            text: '',
          },
          grid: {
            display: true, // hides horizontal grid lines
            borderDash: [5, 5],
          },
        },
        x: {
          grid: {
            display: false, // hides horizontal grid lines
            // borderDash: [5, 5],
          },
        }
      },
    };
  }, [cumulativeByMonthData]);

  useEffect(() => {
    setRefetchFunc(() => refetch);
  }, [refetch]);

  return (
    <Spin spinning={isFetching}>
      <Card type="inner">
        <div className="p-home_chart">
          <h1
            style={{
              fontSize: '20px',
              fontWeight: '600',
              color: '#3D424F',
              marginBottom: '24px'
            }}
          >
            {t('dashboard2025.accumulativeByMonth')}
          </h1>
          {isErrorTimeout ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%'
              }}
            >
              <Typography.Title level={4}>
                {t('message.errorTimeout')}
              </Typography.Title>
            </div>
          ) : (
            <ChartCustom
              data={data}
              type="line"
              options={options}
              plugins={[ChartDataLabels]}
            />
          )}
        </div>
        <div
          className="u-mt-20"
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              border: '1px solid #D7DAE0',
              padding: 4,
              width: 'fit-content',
              borderRadius: '6px',
            }}
          >
            <span
              style={{
                width: '14px',
                height: '14px',
                borderRadius: '4px',
                backgroundColor: '#6A02D5'
              }}
            />
            <div style={{ textAlign: 'center', fontSize: 14 }}>{t('dashboard2025.cumulative')}</div>
          </div>
        </div>
      </Card>
    </Spin>
  );
};
export default NationwideByAccum;